import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import React from "react";
import {
  initialOrgsData,
  organizationsDataReducer,
} from "features/administration/reducers/OrgsDataReducer";
import {
  getOrganizationMembers,
  getOrganizationRobots,
} from "shared/services/organizations_services";
import { useAuth } from "shared/contexts/AuthProvider";
import { servicesUrlConfig } from "shared/config/setting";
import logger from "shared/utils/logging";

const OrganizationsDataContext = createContext();

export function OrganizationsDataProvider({ children }) {
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState("");

  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  const [orgDetailLoadingError, setOrgDetailLoadingError] = useState("");

  const [isMyOrgsDataLoaded, setIsMyOrgsDataLoaded] = useState(false);
  const [isAdminOrgsDataLoaded, setIsAdminOrgsDataLoaded] = useState(false);

  const [organizationsData, dispatch] = useReducer(
    organizationsDataReducer,
    initialOrgsData,
  );

  const {
    auth,
    isAuthenticated,
    redirectToLoginPage,
    user,
    isSuperUser,
    authFetch,
  } = useAuth();

  const history = useHistory();
  const location = useLocation();

  const getAdminOrganizationsList = async () => {
    if (user && isSuperUser) {
      setDataLoading(true);
      logger.debug("Fetching organizations data...");
      setError("");
      // const data = await getOrganizationsListService(auth.token);
      authFetch(servicesUrlConfig.ORGS_API.get_all)
        .then(async (response) => {
          if (response.status === 200) {
            const data = response.data;
            dispatch({ type: "SET_ORGS_DATA", payload: data });
            setIsAdminOrgsDataLoaded(true);
          } else {
            setError("Failed to fetch organizations data");
            throw new Error("Failed to fetch organizations data");
          }
        })
        .catch((err) => {
          logger.error("Error fetching organizations data", err);
          setError(err.message || "Failed to fetch organizations data");
        })
        .finally(() => {
          setDataLoading(false);
        });
    } else {
      dispatch({ type: "SET_ORGS_DATA", payload: [] });
      setIsAdminOrgsDataLoaded(false);
    }
  };

  const loadMyOrganizationsRoles = async (page, perPage) => {
    if (!isAuthenticated) {
      logger.warn(
        "User is not authenticated, skipping fetching my organizations roles data.",
      );
      dispatch({ type: "SET_MY_ORGS_ROLES_DATA", payload: null });
      setIsMyOrgsDataLoaded(false);
      return;
    }
    setDataLoading(true);
    logger.debug("Fetching my organizations data...");
    setError("");
    const parrams = new URLSearchParams();
    if (page && perPage) {
      parrams.append("page", page);
      parrams.append("per_page", perPage);
    }
    authFetch(servicesUrlConfig.ORGS_API.me, {
      parrams: parrams,
    })
      .then(async (response) => {
        const data = response.data;
        logger.debug("Fetching my organizations roles data done ...", data);
        dispatch({ type: "SET_MY_ORGS_ROLES_DATA", payload: data });
        setIsMyOrgsDataLoaded(true);
      })
      .catch((err) => {
        const errorMessage = err.response?.data?.message || err.message;
        logger.error("Error fetching my organizations roles data", err);
        setError(errorMessage || "Failed to fetch organizations data");
      })
      .finally(() => {
        setDataLoading(false);
      });
  };

  const getOrganizationsDetail = async (orgId) => {
    try {
      setOrgDetailLoading(true);
      logger.debug("Fetching user's organizations ...");
      setOrgDetailLoadingError("");

      const members = await getOrganizationMembers(auth.token, orgId);

      const robots = await getOrganizationRobots(auth.token, orgId);

      const data = {
        org_id: orgId,
        members: members,
        robots: robots,
      };
      dispatch({ type: "SET_CURRENT_ORG_DETAIL", payload: data });
    } catch (error) {
      logger.error(error);
      setOrgDetailLoadingError(
        error.message || "Failed to fetch organizations details",
      );
    } finally {
      setOrgDetailLoading(false);
    }
  };

  const getOrgMembersPaged = async (orgId, page = 1, perPage = 10) => {
    try {
      setOrgDetailLoading(true);
      setOrgDetailLoadingError("");
      logger.debug(`Fetching org members page=${page} per_page=${perPage} ...`);
      const members = await getOrganizationMembers(auth.token, orgId, page, perPage);
      dispatch({ type: "UPDATE_CURRENT_ORG_MEMBERS", payload: members });
    } catch (error) {
      logger.error(error);
      setOrgDetailLoadingError(
        error.message || "Failed to fetch organization members",
      );
    } finally {
      setOrgDetailLoading(false);
    }
  };

  const getOrgRobotsPaged = async (orgId, page = 1, perPage = 10) => {
    try {
      setOrgDetailLoading(true);
      setOrgDetailLoadingError("");
      logger.debug(`Fetching org robots page=${page} per_page=${perPage} ...`);
      const robots = await getOrganizationRobots(auth.token, orgId, page, perPage);
      dispatch({ type: "UPDATE_CURRENT_ORG_ROBOTS", payload: robots });
    } catch (error) {
      logger.error(error);
      setOrgDetailLoadingError(
        error.message || "Failed to fetch organization robots",
      );
    } finally {
      setOrgDetailLoading(false);
    }
  };

  // useEffect(() => {
  //   if (!user) {
  //     return;
  //   }
  //   if (!isSuperUser) {
  //     redirectToLoginPage(location);
  //     return;
  //   }
  //   getAdminOrganizationsList();
  // }, [user, isSuperUser]);

  const myOrgsRoles = organizationsData.myOrgsRoles || {
    page: 1,
    per_page: 10,
    total: 0,
    results: [],
  };

  const currentOrgDetail = organizationsData.currentOrgDetail;

  return (
    <OrganizationsDataContext.Provider
      value={{
        organizationsData: organizationsData,
        myOrgsRoles,
        currentOrgDetail,
        error,
        dispatch,
        dataLoading,
        getOrganizationsList: getAdminOrganizationsList,
        getOrganizationsDetail,
        getOrgMembersPaged,
        getOrgRobotsPaged,
        loadMyOrganizationsRoles,
      }}
    >
      {children}
    </OrganizationsDataContext.Provider>
  );
}

export const useOrganizationData = () => useContext(OrganizationsDataContext);
